import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isArray from 'lodash/isArray';

import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'src/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useMediaQuery, Grid } from '@mui/material';
import { useTranslation } from '../../src/i18n';
import Button from '../Button';
import { fontSize, colors } from '../../constants/styleConstants';
import FbPagination from '../FbPagination';
import FbCircularProgress from '../FbCircularProgress';

export const useFBTableStyles = makeStyles()((theme, props) => ({
  table: {
    width: '100%',
    overflow: 'auto',
  },
  cell: {
    verticalAlign: 'middle',
    padding: 10,
    fontSize: fontSize.table,
    borderBottom: `1px solid ${colors.linesGray}`,
    position: 'relative',
    textDecoration: props?.isCancelled ? 'line-through' : 'none',
    backgroundColor: props?.isCancelled ? colors.redLightenCancel : '',
    wordBreak: 'break-all',
  },
  number: {
    verticalAlign: 'top',
    paddingTop: 20,
  },
  flexCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  flexInlineCell: {
    display: 'flex',
    alignItems: 'center',
  },
  head: {
    height: '2em',
    fontSize: fontSize.table,
    padding: 8,
    lineHeight: '1em',
    borderTop: `1px solid ${colors.linesGray}`,
    borderBottom: `1px solid ${colors.linesGray}`,
    color: colors.clearGray,
    textTransform: 'uppercase',
    backgroundColor: colors.background.gray,
  },
  link: {
    color: colors.blue,
    textDecoration: 'underline',
  },
  seeMoreButton: {
    width: '100%',
    backgroundColor: colors.background.gray,
    color: colors.blue,
    [theme.breakpoints.down('md')]: {
      backgroundColor: 'transparent',
    },
  },
  bold: {
    fontWeight: 'bold',
  },
  avatar: {
    width: 45,
    height: 45,
    borderRadius: '50%',
    marginRight: 20,
  },
  flex: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  artistName: {
    width: '40%',
    maxWidth: 220,
  },
  empty: {
    paddingLeft: 10,
  },
  verticalMenu: {
    cursor: 'pointer',
  },
  body: {
    position: 'relative',
  },
  loadingIndicator: {
    position: 'absolute',
    zIndex: 1,
    backgroundColor: colors.background.lightGrey2,
    height: '100%',
    alignItems: 'center',
  },
}));

const FbTable = ({
  headers,
  data,
  cells,
  customElements,
  fetchFunction,
  withPagination,
  noButton,
  loading,
  tableClasses = {},
}) => {
  const { classes, cx } = useFBTableStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  const [tableData, setData] = React.useState(get(data, 'data', []));
  const [isViewAllClicked, setViewAll] = React.useState(false);
  const [isCollapsed, setCollapsed] = React.useState(true);

  // for first fetch
  useEffect(() => {
    const entryData = get(data, 'data', []);
    if (withPagination || noButton) {
      setData(entryData);
    } else {
      setData(isCollapsed ? entryData.slice(0, 4) : entryData);
    }
  }, [data, isCollapsed, withPagination, noButton]);

  const getValueFromArray = (item, array) => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < array.length; i++) {
      const value = get(item, array[i]);
      if (value) return value;
    }
    return null;
  };

  let customRow;
  if (mobileView) {
    customRow = customElements?.card ?? customElements.row ?? null;
  } else {
    customRow = customElements?.row ?? null;
  }
  const { limit, total, page } = data;

  if (tableData.length === 0) {
    return t('operabase.general.NO_RESULTS');
  }

  const showMore = () => {
    // fetch additional data for the first click
    if (isViewAllClicked) {
      setData(isCollapsed ? get(data, 'data', []) : tableData.slice(0, 4));
    } else {
      fetchFunction({ limit: get(data, 'total') });
      setViewAll(true);
    }
    setCollapsed(!isCollapsed);
  };

  return (
    <>
      {mobileView ? (
        <Grid container spacing={2}>
          {loading && <FbCircularProgress />}
          {tableData &&
            tableData.length > 0 &&
            tableData.map(card => (
              <Grid key={get(data, 'id')} item sm={12} xs={12}>
                {React.cloneElement(customRow, {
                  data: card,
                })}
              </Grid>
            ))}
        </Grid>
      ) : (
        <div className={classes.table}>
          <Table classes={{ root: tableClasses.tableRoot }}>
            <TableHead classes={{ root: tableClasses.tableHead }}>
              <TableRow>
                {headers &&
                  headers.length > 0 &&
                  headers?.map((item, index) => (
                    <TableCell
                      classes={{ head: classes.head, root: tableClasses.tableHeaderCell }}
                      key={`${get(item, 'props.id') || item}-${index}`}
                    >
                      {item}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody classes={{ root: cx(tableClasses.tableBody, classes.body) }}>
              {loading && <FbCircularProgress className={classes.loadingIndicator} />}
              {tableData &&
                tableData.length > 0 &&
                tableData.map((row, index) =>
                  customRow ? (
                    React.cloneElement(customRow, {
                      data: row,
                      cellClass: classes.cell,
                      key: get(row, 'id') || `FbTable-${index}`,
                    })
                  ) : (
                    <TableRow key={get(row, 'id') || index} classes={{ root: tableClasses.tableRow }}>
                      {cells.map(item => (
                        <TableCell
                          className={classes.cell}
                          classes={{ root: tableClasses.tableCell }}
                          key={get(row, item) || get(row, `[0].${item}`)}
                        >
                          {isArray(item) ? getValueFromArray(row, item) : get(row, item, '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ),
                )}
            </TableBody>
          </Table>
        </div>
      )}
      {withPagination && <FbPagination filters={{ limit, total, page }} fetchFunction={fetchFunction} />}
      {!withPagination && !noButton && (
        <Button onClick={showMore} className={classes.seeMoreButton}>
          {t(isCollapsed ? 'operabase.general.m_ARTICLES_SEE_ALL' : 'operabase.general.FN_SEE_LESS_TITLE')}
        </Button>
      )}
    </>
  );
};

FbTable.propTypes = {
  data: PropTypes.object,
  headers: PropTypes.array,
  cells: PropTypes.array,
  customElements: PropTypes.shape({
    card: PropTypes.element,
    row: PropTypes.element,
  }),
  fetchFunction: PropTypes.func,
  withPagination: PropTypes.bool,
  noButton: PropTypes.bool,
  loading: PropTypes.bool,
  tableClasses: PropTypes.shape({
    tableRoot: PropTypes.string,
    tableHead: PropTypes.string,
    tableHeaderCell: PropTypes.string,
    tableBody: PropTypes.string,
    tableRow: PropTypes.string,
    tableCell: PropTypes.string,
  }),
};

FbTable.defaultProps = {
  data: {
    total: 0,
    limit: 100,
    page: 1,
    data: [],
  },
  headers: [],
  withPagination: true,
  fetchFunction: () => {},
  noButton: false,
  loading: false,
};

export default FbTable;
